import * as XLSX from "xlsx";
import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  changeCustomerActiveAgentService,
  changeCustomerPasswordAgentService,
  createCustomerAgentService,
  createTransferTransactionAgentService,
  deleteCustomerAgentService,
  editCustomerAgentService,
  getCustomersAgentService,
} from "./../../../services/agentServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { validateFile } from "../../../utils";
import { uploadTypeItems } from "../../../utils/enum";
import { uploadUserService } from "../../../services/userServices";
import { setUserAction } from "./../../../redux/actions/userActions/userActions";

export const customerAgentContext = createContext({
  customers: [],
  setCustomers: () => {},
  customer: {},
  setCustomer: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  role: "",
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetCustomers: () => {},
  handleCreateCustomer: () => {},
  handleEditCustomer: () => {},
  handleChangeCustomerPassword: () => {},
  handleChangeCustomerActive: () => {},
  handleDeleteCustomer: () => {},
  handleUploadFile: () => {},
  handleCreateTransferTransaction: () => {},
  handleAddMultipleUsers: () => {},
});

const CustomerAgentContext = ({ children }) => {
  const dispatch = useDispatch();

  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setCustomer(val);
      setValue("avatar", val.avatar);
      setValue("firstName", val.firstName);
      setValue("lastName", val.lastName);
      setValue("phoneNumber", val.phoneNumber);
      setValue("nationalNumber", val.nationalNumber);
      setValue("code", val.code);
      setValue("birthDate", val.birthDate);
      setValue("post", val.post);
      setValue("numberOfCovered", val.numberOfCovered);
      setValue("email", val.email);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setCustomer({});
  };

  // Get Customers
  const handleGetCustomers = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCustomersAgentService(objData);
      if (status === 200) {
        setCustomers(data.result?.users);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Customer
  const handleCreateCustomer = async (customerData) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createCustomerAgentService(customerData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCustomers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Customer
  const handleEditCustomer = async (customerData) => {
    const objData = {
      id: customer._id,
      ...customerData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editCustomerAgentService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCustomers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Change Customer Password
  const handleChangeCustomerPassword = async (customerData) => {
    const objData = {
      id: customer._id,
      ...customerData,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeCustomerPasswordAgentService(
        objData
      );
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        setCustomer({});
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Change Customer Active
  const handleChangeCustomerActive = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeCustomerActiveAgentService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCustomers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Customer
  const handleDeleteCustomer = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteCustomerAgentService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCustomers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Upload File
  const handleUploadFile = async (file, callback) => {
    const fileErrorMessage = validateFile(file);
    if (fileErrorMessage) return toastErrorMessage(fileErrorMessage);

    const objData = { file };
    const params = { destination: uploadTypeItems[1] };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await uploadUserService(objData, params);
      if (status === 200) {
        callback && callback(data.result);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Transfer Transaction
  const handleCreateTransferTransaction = async (userData) => {
    const objData = {
      ...userData,
      customer: customer._id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createTransferTransactionAgentService(
        objData
      );
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetCustomers();
        handleHideModal();
        dispatch(setUserAction());
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Add Multiple Users
  const handleAddMultipleUsers = async (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const users = XLSX.utils.sheet_to_json(worksheet);

      for (const user of users) {
        setTimeout(() => {
          handleCreateCustomer(user);
        }, 500);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <customerAgentContext.Provider
      value={{
        customers,
        setCustomers,
        customer,
        setCustomer,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetCustomers,
        handleCreateCustomer,
        handleEditCustomer,
        handleChangeCustomerPassword,
        handleChangeCustomerActive,
        handleDeleteCustomer,
        handleUploadFile,
        handleCreateTransferTransaction,
        handleAddMultipleUsers,
      }}
    >
      {children}
    </customerAgentContext.Provider>
  );
};

export default CustomerAgentContext;
